import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Link as LinkIcon, Car, ListChecks, Hash } from "lucide-react";

const MAX_VEHICLES = 50;

// Parse a "paste VINs" textarea into synthetic `vin:VIN?mileage=…` tokens.
// Each non-empty line looks like: `VIN` OR `VIN,mileage` OR `VIN mileage`.
// VINs are 17 chars, alphanumeric (no I/O/Q by spec but we don't strict-check).
const parseVinLines = (text) => {
  const tokens = [];
  const lines = (text || "").split(/\n+/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(/[\s,;|\t]+/).filter(Boolean);
    const vin = (parts[0] || "").toUpperCase();
    if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(vin)) continue;
    const miles = parts.slice(1).map((p) => p.replace(/[^\d]/g, "")).find((p) => p) || "";
    tokens.push(miles ? `vin:${vin}?mileage=${miles}` : `vin:${vin}`);
  }
  return tokens;
};

export default function NewProposal() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [dealership, setDealership] = useState("");
  const [mode, setMode] = useState("multi"); // "single" | "multi" | "vins"
  const [singleUrl, setSingleUrl] = useState("");
  const [urlsText, setUrlsText] = useState("");
  const [vinsText, setVinsText] = useState("");
  const [scrapeLimit, setScrapeLimit] = useState(15);
  const [loading, setLoading] = useState(false);

  const parseMultiUrls = () =>
    urlsText
      .split(/[\s\n,]+/)
      .map((s) => s.trim())
      .filter((s) => /^(https?:\/\/|vin:)/i.test(s));

  const vinTokens = parseVinLines(vinsText);

  const currentUrls =
    mode === "single"
      ? (/^(https?:\/\/|vin:)/i.test(singleUrl.trim()) ? [singleUrl.trim()] : [])
      : mode === "vins"
        ? vinTokens
        : parseMultiUrls();

  const effectiveLimit =
    mode === "single" ? 1 : Math.min(Math.max(1, scrapeLimit || 1), MAX_VEHICLES);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Please add a proposal title"); return; }
    if (currentUrls.length === 0) {
      toast.error(mode === "vins"
        ? "Paste at least one 17-character VIN (one per line)"
        : "Add at least one vehicle or listing URL");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        dealership: dealership.trim() || null,
        urls: currentUrls.slice(0, effectiveLimit),
        max_vehicles: effectiveLimit,
      };
      const { data } = await api.post("/proposals", payload);
      toast.success(`${data.vehicles?.length || 0} vehicles quoted`);
      nav(`/proposals/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create proposal");
    } finally {
      setLoading(false);
    }
  };

  const inputCount = currentUrls.length;
  const overCap = (mode === "multi" || mode === "vins") && inputCount > MAX_VEHICLES;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#6C757D] hover:text-[#0B132B] transition-colors mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Step 01 — Setup
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">
            Build a new proposal
          </h1>
          <p className="text-sm text-[#6C757D] mt-3 max-w-xl leading-relaxed">
            Quote a single vehicle, bulk-scrape up to <strong>50</strong> from a dealership page,
            or paste raw VINs when a site is blocked by anti-bot protection.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="new-proposal-form">
          <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8 space-y-6">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Proposal title</Label>
              <Input
                required value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Cannon Honda — Certified Pre-Owned Pitch"
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                data-testid="proposal-title-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                Dealership <span className="text-[#6C757D] normal-case tracking-normal">(optional)</span>
              </Label>
              <Input
                value={dealership} onChange={(e) => setDealership(e.target.value)}
                placeholder="e.g. Cannon Honda of Ponca City"
                className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                data-testid="proposal-dealership-input"
              />
            </div>
          </Card>

          {/* Mode tabs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="vehicle-mode-tabs">
            {[
              { key: "single", label: "Single vehicle",    hint: "One VDP URL",                   icon: Car },
              { key: "multi",  label: "Multiple vehicles", hint: "Inventory page or many URLs",   icon: ListChecks },
              { key: "vins",   label: "Paste VINs",        hint: "Bypass blocked dealer sites",   icon: Hash },
            ].map((tab) => {
              const on = mode === tab.key;
              const Icon = tab.icon;
              return (
                <button
                  type="button"
                  key={tab.key}
                  onClick={() => setMode(tab.key)}
                  className={`flex items-start gap-3 px-5 py-4 rounded-sm border-2 text-left transition-colors ${
                    on ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#E9ECEF] bg-white hover:border-[#0B132B]/30"
                  }`}
                  data-testid={`mode-tab-${tab.key}`}
                >
                  <Icon size={20} className={on ? "text-[#D4AF37]" : "text-[#6C757D]"} />
                  <div>
                    <div className="font-bold text-sm text-[#0B132B]">{tab.label}</div>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6C757D] mt-0.5">{tab.hint}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8">
            {mode === "single" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] flex items-center gap-2">
                  <LinkIcon size={14} /> Vehicle URL
                </Label>
                <Input
                  value={singleUrl}
                  onChange={(e) => setSingleUrl(e.target.value)}
                  placeholder="https://www.dealer.com/inventory/2022-ford-f150-…"
                  className="mt-2 h-11 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37] font-mono text-xs"
                  data-testid="single-url-input"
                />
                <p className="text-xs text-[#6C757D] mt-3 leading-relaxed">
                  Paste one Vehicle Detail Page (VDP) URL. We'll scrape VIN, mileage, photo, and generate quotes.
                </p>
              </div>
            )}

            {mode === "multi" && (
              <>
                <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] flex items-center gap-2">
                    <LinkIcon size={14} /> Vehicle URLs
                  </Label>
                  <div className="flex items-center gap-3">
                    <Label htmlFor="scrape-limit" className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
                      Max to scrape
                    </Label>
                    <Input
                      id="scrape-limit"
                      type="number" min={1} max={MAX_VEHICLES}
                      value={scrapeLimit}
                      onChange={(e) => setScrapeLimit(Math.min(MAX_VEHICLES, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-20 h-9 rounded-sm border-[#0B132B]/15 focus-visible:ring-[#D4AF37] text-center font-mono"
                      data-testid="scrape-limit-input"
                    />
                    <span className={`text-xs font-mono ${overCap ? "text-red-600" : "text-[#6C757D]"}`} data-testid="url-count">
                      {inputCount}/{MAX_VEHICLES}
                    </span>
                  </div>
                </div>
                <Textarea
                  value={urlsText}
                  onChange={(e) => setUrlsText(e.target.value)}
                  rows={10}
                  placeholder={"Paste a dealership inventory / search URL to auto-expand:\nhttps://www.luxuryautobr.com/inventory/acura/\n\nor paste up to 50 individual VDP URLs, one per line."}
                  className="rounded-sm font-mono text-xs border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                  data-testid="proposal-urls-textarea"
                />
                <p className="text-xs text-[#6C757D] mt-3 leading-relaxed">
                  One URL per line. Paste a <span className="font-semibold text-[#0B132B]">search / inventory page</span>
                  {" "}and we'll auto-expand up to <span className="font-semibold text-[#0B132B]">{effectiveLimit}</span> vehicles from it.
                </p>
              </>
            )}

            {mode === "vins" && (
              <>
                <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] flex items-center gap-2">
                    <Hash size={14} /> Raw VIN list
                  </Label>
                  <span className={`text-xs font-mono ${overCap ? "text-red-600" : "text-[#6C757D]"}`} data-testid="vin-count">
                    {inputCount}/{MAX_VEHICLES} VIN{inputCount === 1 ? "" : "s"}
                  </span>
                </div>
                <Textarea
                  value={vinsText}
                  onChange={(e) => setVinsText(e.target.value)}
                  rows={10}
                  placeholder={"One VIN per line. Optionally add mileage after a comma or space:\n1FTFW1E85MFA12345\n5NPE24AF6FH123456, 45000\nWDDGF8AB8DA123456  87500"}
                  className="rounded-sm font-mono text-xs border-[#0B132B]/15 focus-visible:ring-[#D4AF37]"
                  data-testid="proposal-vins-textarea"
                />
                <p className="text-xs text-[#6C757D] mt-3 leading-relaxed">
                  Use this when a dealer site is blocked by Cloudflare or is JS-only. Each VIN is decoded via
                  NHTSA for year / make / model / cylinders. Add mileage after the VIN
                  (<span className="font-mono text-[#0B132B]">VIN, 45000</span>) so eligibility gates apply correctly —
                  otherwise you can edit mileage on the proposal after generation.
                </p>
              </>
            )}
          </Card>

          <div className="flex items-center justify-end gap-3">
            <Link to="/dashboard">
              <Button type="button" variant="outline"
                className="h-11 rounded-sm border-[#0B132B]/20" data-testid="cancel-button">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={loading || currentUrls.length === 0}
              className="h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold px-6 transition-colors"
              data-testid="generate-proposal-button"
            >
              <Sparkles size={16} className="mr-2" />
              {loading ? "Generating quotes…" : `Generate quotes (${mode === "single" ? 1 : Math.min(inputCount || effectiveLimit, effectiveLimit)})`}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
